import React, { useMemo, useRef, useState } from 'react';

type DiffLineType = 'add' | 'del' | 'equal' | 'modify';

type DiffLine = {
  type: DiffLineType;
  leftLineNo?: number;
  rightLineNo?: number;
  leftText?: string;
  rightText?: string;
};

type SideBySideDiffProps = {
  originalContent: string;
  modifiedContent: string;
  originalTitle?: string;
  modifiedTitle?: string;
  onClose?: () => void;
};

// Simple diff algorithm for side-by-side view
function computeSideBySideDiff(original: string, modified: string): DiffLine[] {
  const originalLines = original.split('\n');
  const modifiedLines = modified.split('\n');
  const result: DiffLine[] = [];

  // LCS-based diff for better alignment
  const lcs = computeLCS(originalLines, modifiedLines);

  let origIdx = 0;
  let modIdx = 0;
  let lcsIdx = 0;

  while (origIdx < originalLines.length || modIdx < modifiedLines.length) {
    if (lcsIdx < lcs.length &&
        origIdx < originalLines.length &&
        modIdx < modifiedLines.length &&
        originalLines[origIdx] === lcs[lcsIdx] &&
        modifiedLines[modIdx] === lcs[lcsIdx]) {
      // Equal line
      result.push({
        type: 'equal',
        leftLineNo: origIdx + 1,
        rightLineNo: modIdx + 1,
        leftText: originalLines[origIdx],
        rightText: modifiedLines[modIdx],
      });
      origIdx++;
      modIdx++;
      lcsIdx++;
    } else if (lcsIdx < lcs.length &&
               origIdx < originalLines.length &&
               originalLines[origIdx] !== lcs[lcsIdx] &&
               modIdx < modifiedLines.length &&
               modifiedLines[modIdx] !== lcs[lcsIdx]) {
      // Modified line (both different from LCS)
      result.push({
        type: 'modify',
        leftLineNo: origIdx + 1,
        rightLineNo: modIdx + 1,
        leftText: originalLines[origIdx],
        rightText: modifiedLines[modIdx],
      });
      origIdx++;
      modIdx++;
    } else if (origIdx < originalLines.length &&
               (lcsIdx >= lcs.length || originalLines[origIdx] !== lcs[lcsIdx])) {
      // Deleted line
      result.push({
        type: 'del',
        leftLineNo: origIdx + 1,
        leftText: originalLines[origIdx],
      });
      origIdx++;
    } else if (modIdx < modifiedLines.length &&
               (lcsIdx >= lcs.length || modifiedLines[modIdx] !== lcs[lcsIdx])) {
      // Added line
      result.push({
        type: 'add',
        rightLineNo: modIdx + 1,
        rightText: modifiedLines[modIdx],
      });
      modIdx++;
    } else {
      break;
    }
  }

  return result;
}

// Compute Longest Common Subsequence
function computeLCS(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find LCS
  const lcs: string[] = [];
  let i = m, j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return lcs;
}

// Highlight character-level changes within a line
function highlightInlineChanges(oldText: string, newText: string): { oldParts: { text: string; highlight: boolean }[]; newParts: { text: string; highlight: boolean }[] } {
  if (!oldText || !newText) {
    return {
      oldParts: oldText ? [{ text: oldText, highlight: true }] : [],
      newParts: newText ? [{ text: newText, highlight: true }] : [],
    };
  }

  // Find common prefix
  let prefixLen = 0;
  while (prefixLen < oldText.length && prefixLen < newText.length && oldText[prefixLen] === newText[prefixLen]) {
    prefixLen++;
  }

  // Find common suffix
  let suffixLen = 0;
  while (
    suffixLen < oldText.length - prefixLen &&
    suffixLen < newText.length - prefixLen &&
    oldText[oldText.length - 1 - suffixLen] === newText[newText.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  const oldMiddle = oldText.slice(prefixLen, oldText.length - suffixLen);
  const newMiddle = newText.slice(prefixLen, newText.length - suffixLen);
  const prefix = oldText.slice(0, prefixLen);
  const suffix = oldText.slice(oldText.length - suffixLen);

  const oldParts: { text: string; highlight: boolean }[] = [];
  const newParts: { text: string; highlight: boolean }[] = [];

  if (prefix) {
    oldParts.push({ text: prefix, highlight: false });
    newParts.push({ text: prefix, highlight: false });
  }
  if (oldMiddle) {
    oldParts.push({ text: oldMiddle, highlight: true });
  }
  if (newMiddle) {
    newParts.push({ text: newMiddle, highlight: true });
  }
  if (suffix) {
    oldParts.push({ text: suffix, highlight: false });
    newParts.push({ text: suffix, highlight: false });
  }

  return { oldParts, newParts };
}

export const SideBySideDiff = React.memo(function SideBySideDiff({
  originalContent,
  modifiedContent,
  originalTitle = 'Original',
  modifiedTitle = 'Modified',
  onClose,
}: SideBySideDiffProps) {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [syncing, setSyncing] = useState<'left' | 'right' | null>(null);

  const diffLines = useMemo(() => {
    return computeSideBySideDiff(originalContent, modifiedContent);
  }, [originalContent, modifiedContent]);

  // Sync scroll between left and right panels
  const handleScroll = (source: 'left' | 'right') => {
    if (syncing) return;
    setSyncing(source);

    const sourceRef = source === 'left' ? leftRef : rightRef;
    const targetRef = source === 'left' ? rightRef : leftRef;

    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollTop = sourceRef.current.scrollTop;
    }

    requestAnimationFrame(() => setSyncing(null));
  };

  // Calculate stats
  const stats = useMemo(() => {
    let additions = 0;
    let deletions = 0;
    let modifications = 0;

    diffLines.forEach(line => {
      if (line.type === 'add') additions++;
      else if (line.type === 'del') deletions++;
      else if (line.type === 'modify') modifications++;
    });

    return { additions, deletions, modifications };
  }, [diffLines]);

  const renderLine = (line: DiffLine, side: 'left' | 'right') => {
    const isLeft = side === 'left';
    const lineNo = isLeft ? line.leftLineNo : line.rightLineNo;
    const text = isLeft ? line.leftText : line.rightText;

    if (line.type === 'equal') {
      return (
        <div key={`${side}-${lineNo}`} className="diff-line equal">
          <span className="diff-line-no">{lineNo}</span>
          <span className="diff-line-content">{text}</span>
        </div>
      );
    }

    if (line.type === 'modify') {
      const { oldParts, newParts } = highlightInlineChanges(line.leftText || '', line.rightText || '');
      const parts = isLeft ? oldParts : newParts;

      return (
        <div key={`${side}-${lineNo}`} className={`diff-line ${isLeft ? 'del' : 'add'}`}>
          <span className="diff-line-no">{lineNo}</span>
          <span className="diff-line-content">
            {parts.map((part, i) => (
              <span key={i} className={part.highlight ? 'diff-inline-highlight' : ''}>
                {part.text}
              </span>
            ))}
          </span>
        </div>
      );
    }

    if ((line.type === 'del' && isLeft) || (line.type === 'add' && !isLeft)) {
      return (
        <div key={`${side}-${lineNo}`} className={`diff-line ${line.type}`}>
          <span className="diff-line-no">{lineNo}</span>
          <span className="diff-line-content">{text}</span>
        </div>
      );
    }

    // Empty placeholder for alignment
    return (
      <div key={`${side}-empty-${line.leftLineNo || line.rightLineNo}`} className="diff-line empty">
        <span className="diff-line-no"></span>
        <span className="diff-line-content"></span>
      </div>
    );
  };

  return (
    <div className="side-by-side-diff">
      <div className="diff-header">
        <div className="diff-stats">
          <span className="diff-stat additions">+{stats.additions}</span>
          <span className="diff-stat deletions">-{stats.deletions}</span>
          {stats.modifications > 0 && (
            <span className="diff-stat modifications">~{stats.modifications}</span>
          )}
        </div>
        {onClose && (
          <button className="diff-close-btn" onClick={onClose} title="Close diff view">
            ×
          </button>
        )}
      </div>

      <div className="diff-titles">
        <div className="diff-title left">{originalTitle}</div>
        <div className="diff-title right">{modifiedTitle}</div>
      </div>

      <div className="diff-content">
        <div
          ref={leftRef}
          className="diff-pane left"
          onScroll={() => handleScroll('left')}
        >
          {diffLines.map((line, idx) => renderLine(line, 'left'))}
        </div>
        <div className="diff-divider" />
        <div
          ref={rightRef}
          className="diff-pane right"
          onScroll={() => handleScroll('right')}
        >
          {diffLines.map((line, idx) => renderLine(line, 'right'))}
        </div>
      </div>
    </div>
  );
});
